import React from 'react';
import {gql, useQuery} from '@apollo/client';
import {Box, Grid} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import {StickyDataGrid} from '../../components/Common/StickyDataGrid';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';

export const IDENTITY_USER_HISTORY_QUERY = gql`
  query IdentityUserHistory($subId: String!) {
    identityUserHistory(subId: $subId) {
      history {
        creationDate
        eventId
        eventType
        eventResponse
        eventRisk {
          riskLevel
          riskDecision
        }
        eventContextData {
          ipAddress
          deviceName
          city
          country
        }
      }
      ok
      errors
    }
  }
`;

export const useIdentityUserHistory = (subId?: string) =>
  useQuery(IDENTITY_USER_HISTORY_QUERY, {
    variables: {subId: subId || ''},
    skip: !subId,
  });

const historyColumns: GridColDef[] = [
  {
    field: 'creationDate',
    headerName: 'Date',
    flex: 1,
    type: 'dateTime',
    valueGetter: (value) => value && new Date(value),
  },
  {
    field: 'eventType',
    headerName: 'Event Type',
    flex: 1,
  },
  {
    field: 'eventResponse',
    headerName: 'Response',
    flex: 0.8,
  },
  {
    field: 'riskLevel',
    headerName: 'Risk Level',
    flex: 0.8,
    valueGetter: (value, row) => row.eventRisk?.riskLevel || '',
  },
  {
    field: 'riskDecision',
    headerName: 'Risk Decision',
    flex: 0.8,
    valueGetter: (value, row) => row.eventRisk?.riskDecision || '',
  },
  {
    field: 'ipAddress',
    headerName: 'IP Address',
    flex: 0.8,
    valueGetter: (value, row) => row.eventContextData?.ipAddress || '',
  },
  {
    field: 'deviceName',
    headerName: 'Device',
    flex: 1,
    valueGetter: (value, row) => row.eventContextData?.deviceName || '',
  },
  {
    field: 'location',
    headerName: 'Location',
    flex: 1,
    valueGetter: (value, row) => {
      const city = row.eventContextData?.city;
      const country = row.eventContextData?.country;
      if (city && country) return `${city}, ${country}`;
      return city || country || '';
    },
  },
];

export const IdentityHistory: React.FC<{subId?: string}> = ({subId}) => {
  const {data: historyData, loading: historyLoading} = useIdentityUserHistory(subId);

  return (
    <Grid item xs={12}>
      <Panel title="History" loading={historyLoading} color={themeColors.lightPink}>
        <Box height={500}>
          <StickyDataGrid
            rows={historyData?.identityUserHistory?.history || []}
            columns={historyColumns}
            getRowId={(row) => row.eventId}
            pagination
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              sorting: {
                sortModel: [{field: 'creationDate', sort: 'desc'}],
              },
            }}
            pageSizeOptions={[10, 25, 50]}
          />
        </Box>
      </Panel>
    </Grid>
  );
};
